import type { SipCallData } from "@/components/call";

import type { WsInboundEnvelope } from "./wsInboundEnvelope";
import {
  optBool,
  optCallerId,
  optFiniteNumber,
  optString,
} from "./wsPayloadCoercion";

/** Coerces known SIP notify `sip_data` keys; ignores unexpected fields. */
function parsePartialSipCallData(
  raw: unknown,
): Partial<SipCallData> | undefined {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const s = raw as Record<string, unknown>;
  const out: Partial<SipCallData> = {};

  const set = <K extends keyof SipCallData>(key: K, val: SipCallData[K]) => {
    if (val !== undefined) out[key] = val;
  };

  set("call_log_id", optString(s.call_log_id));
  set("provider_call_id", optString(s.provider_call_id));
  const cid = optFiniteNumber(s.client_id);
  if (cid !== undefined) set("client_id", cid);
  set("livekit_room", optString(s.livekit_room));
  set("livekit_token", optString(s.livekit_token));
  set("livekit_host", optString(s.livekit_host));
  set("did_number", optString(s.did_number));
  set("extension_number", optString(s.extension_number));
  set("extension_display_name", optString(s.extension_display_name));
  set("target_extension", optString(s.target_extension));
  set("trunk_id", optString(s.trunk_id));
  set("trunk_name", optString(s.trunk_name));
  set("ring_group_id", optString(s.ring_group_id));
  set("ring_group_name", optString(s.ring_group_name));
  set("ring_strategy", optString(s.ring_strategy));
  set("caller_name", optString(s.caller_name));
  set("caller_number", optString(s.caller_number));
  set("queue_id", optString(s.queue_id));
  set("queue_name", optString(s.queue_name));

  const rt = optFiniteNumber(s.ring_timeout);
  if (rt !== undefined) set("ring_timeout", rt);
  const qp = optFiniteNumber(s.queue_position);
  if (qp !== undefined) set("queue_position", qp);

  return Object.keys(out).length > 0 ? out : undefined;
}

export type IncomingCallLikeWsType =
  | "sip_incoming_call"
  | "operations_incoming_call"
  | "incoming_call";

/** Narrowed fields used by handleTotalConnectRtcWebSocketMessage incoming-call arm. */
export interface IncomingCallLikeWsPayload {
  type: IncomingCallLikeWsType;
  call_id?: string;
  call_log_id?: string;
  room_name?: string;
  client_id?: number;
  caller_id?: number;
  caller_id_number?: string;
  caller_number?: string;
  caller_id_name?: string;
  caller_name?: string;
  caller_avatar?: string;
  /** Raw string from server (`voice`, `video`, `sip_inbound`, …). */
  call_type?: string;
  is_operations_call?: boolean;
  is_sip_call?: boolean;
  /** True for visitor-management gate calls (DTMF authorize/deny). */
  gate_call?: boolean;
  sip_data?: Partial<SipCallData>;
}

/** Validated only for the three incoming-call event types. */
export function parseIncomingCallLikePayload(
  envelope: WsInboundEnvelope,
): IncomingCallLikeWsPayload | null {
  const t = envelope.type;
  if (
    t !== "sip_incoming_call" &&
    t !== "operations_incoming_call" &&
    t !== "incoming_call"
  ) {
    return null;
  }

  const o = envelope;

  return {
    type: t,
    call_id: optString(o.call_id),
    call_log_id: optString(o.call_log_id),
    room_name: optString(o.room_name),
    client_id: optFiniteNumber(o.client_id),
    caller_id: optCallerId(o.caller_id),
    caller_id_number: optString(o.caller_id_number),
    caller_number: optString(o.caller_number),
    caller_id_name: optString(o.caller_id_name),
    caller_name: optString(o.caller_name),
    caller_avatar: optString(o.caller_avatar),
    call_type: optString(o.call_type),
    is_operations_call: optBool(o.is_operations_call),
    is_sip_call: optBool(o.is_sip_call),
    gate_call: optBool(o.gate_call),
    sip_data: parsePartialSipCallData(o.sip_data),
  };
}
